import { differenceInMinutes, format } from 'date-fns';

import { ProductType } from '@/components/products/ProductsPromptModal/redux/types';
import { formatPrice, getCurrencySymbol } from '@/components/products/utils';
import { ParsedUserProduct, Product } from '@/queries/products/types';

export const getProductTitle = (productLookupKey: string | undefined, habitatName?: string) => {
  switch (productLookupKey) {
    case 'single-habitat-day-pass':
      return `Day Pass: ${habitatName || 'Unknown Habitat'}`;
    case 'all-habitats-day-pass':
      return 'All-Access Day Pass';
    case 'monthly-supporter-tier-1':
    case 'monthly-supporter-tier-2':
    case 'monthly-supporter-tier-3':
      return `Monthly ${habitatName || 'Unknown Habitat'} Supporter`;
    case 'annual-supporter-tier-1':
    case 'annual-supporter-tier-2':
    case 'annual-supporter-tier-3':
      return `Yearly ${habitatName || 'Unknown Habitat'} Supporter`;
    case 'monthly-member':
      return 'Monthly Explorer Plan';
    case 'annual-member':
      return 'Yearly Explorer Plan';
    case 'monthly-educator':
      return 'Monthly Educator Plan';
    case 'annual-educator':
      return 'Yearly Educator Plan';
    default:
      return productLookupKey || 'Unknown Product';
  }
};

export const toProductType = (productLookupKey: string | undefined): ProductType | undefined => {
  switch (productLookupKey) {
    case 'single-habitat-day-pass':
      return 'single-habitat-day-pass';
    case 'all-habitats-day-pass':
      return 'all-habitats-day-pass';

    case 'monthly-supporter-tier-1':
    case 'monthly-supporter-tier-2':
    case 'monthly-supporter-tier-3':
    case 'annual-supporter-tier-1':
    case 'annual-supporter-tier-2':
    case 'annual-supporter-tier-3':
      return 'sponsorship';

    case 'monthly-member':
    case 'annual-member':
      return 'explorer-pass';

    case 'monthly-educator':
    case 'annual-educator':
      return 'educator-pass';
    default:
      return undefined;
  }
};

export const getProductDescription = (userProduct: ParsedUserProduct, now: number) => {
  const { product, validUntil } = userProduct;
  const productLookupKey = product?.productLookupKey;

  const isDayPass = ['single-habitat-day-pass', 'all-habitats-day-pass'].includes(productLookupKey || '');
  if (validUntil < now) {
    return `Expired ${format(validUntil, isDayPass ? 'h:mm a MMM d yyyy' : 'MMMM d yyyy')}`;
  }
  if (isDayPass) {
    const totalMinutes = differenceInMinutes(validUntil, now);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `Expiring in ${hours > 0 ? `${hours}h ` : ''}${minutes}m`;
  }
  if (!userProduct.scheduledProductId) {
    return `Scheduled cancel on ${format(validUntil, 'MMMM d')}`;
  }
  if (userProduct.scheduledProductId !== userProduct.productId) {
    return `Scheduled change on ${format(validUntil, 'MMMM d')}`;
  }
  return (
    <>
      Renews {format(validUntil, 'MMMM d')}
      <span className="mobile:hidden"> {format(validUntil, 'yyyy')}</span>
    </>
  );
};

export const getProductPrice = (priceObject: Pick<Product['price'], 'currency' | 'price' | 'interval'> | undefined) => {
  if (!priceObject) return undefined;
  const price = `${getCurrencySymbol(priceObject.currency)} ${formatPrice(priceObject.price ?? 0)}`;
  if (priceObject.interval === 'month') {
    return `${price}/Mo`;
  }
  if (priceObject.interval === 'year') {
    return `${price}/Yr`;
  }
  if (priceObject.interval === 'week') {
    return `${price}/Wk`;
  }
  return price;
};

export const getProductMonthlyPrice = (
  priceObject: Pick<Product['price'], 'currency' | 'price' | 'interval'> | undefined,
) => {
  if (!priceObject) return undefined;
  const price = priceObject.interval === 'month' ? priceObject.price : Math.floor(priceObject.price / 12);
  return `${getCurrencySymbol(priceObject.currency)}${formatPrice(price)}/Mo`;
};
